import { MutableRefObject, useCallback, memo, useState, useRef } from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList
} from "@/components/adg4/tabs/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

import { TWC_TAB_DATA, TWCTab } from "./twc-tab-data";

const slideInFromLeft = keyframes({
    from: {
        opacity: 0,
        transform: "translateX(-100%)"
    },
    to: {
        opacity: 1,
        transform: "translateX(0)"
    }
});

const slideInFromRight = keyframes({
    from: {
        opacity: 0,
        transform: "translateX(100%)"
    },
    to: {
        opacity: 1,
        transform: "translateX(0)"
    }
});

const slideOutToLeft = keyframes({
    from: {
        opacity: 1,
        transform: "translateX(0)"
    },
    to: {
        opacity: 0,
        transform: "translateX(-100%)"
    }
});

const slideOutToRight = keyframes({
    from: {
        opacity: 1,
        transform: "translateX(0)"
    },
    to: {
        opacity: 0,
        transform: "translateX(100%)"
    }
});

const tabsWrapperStyle = css({
    marginInlineStart: "8px",
    marginInlineEnd: "8px"
});

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "10px"
});
const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});
const fadeIn = keyframes({
    from: {
        opacity: 0
    },
    to: {
        opacity: 1
    }
});

const fadeInSlideUp = keyframes({
    from: {
        opacity: 0,
        transform: "translateY(10px)"
    },
    to: {
        opacity: 1,
        transform: "translateY(0)"
    }
});

const teamCardStyles = css({
    "boxSizing": "border-box",
    "border": `1px solid transparent`,
    "display": "flex",
    "flexDirection": "column",
    "width": "187px",
    "height": "129px",
    "alignItems": "center",
    "justifyContent": "space-between",
    "padding": "12px 8px 19px",

    [media.below.sm]: {
        width: "155px"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".gesture-icon": {
            animation: `${fadeIn} 0.5s ease-out forwards`
        }
    },

    "&[data-gesture-slide-up='true']:hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".gesture-icon": {
            animation: `${fadeInSlideUp} 0.5s ease-out forwards`
        }
    }
});

const teamCardActiveStyles = css({
    "border": `1px solid ${wacToken("color.primary.black")}`,
    "borderRadius": "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".gesture-icon": {
        opacity: 1
    }
});

const iconWrapperStyles = css({
    position: "relative"
});

const iconContainerStyles = css({
    height: "48px",
    width: "48px"
});

const iconGestureStyles = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    opacity: 0
});

const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: wacToken("color.primary.black")
});

const imageContainerStyles = css({
    width: "100%",
    aspectRatio: "16/9",
    position: "relative",
    marginBlockEnd: "8%"
});
const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "47%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: wacToken("color.primary.blue"),
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 16%,49% 10%,50% 26%,98.5% 13%,100% 85%,22.5% 100%,22.25% 85%,0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});

const heroImageStylesLeft = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        opacity: 0,
        aspectRatio: "16/9",
        position: "absolute",
        maxWidth: "100%",
        maxHeight: "100%"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            animation: `${slideInFromLeft} 0.3s ease-out forwards`
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image-initial": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            opacity: 1
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".exiting-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            animation: `${slideOutToLeft} 0.3s ease-out forwards`
        }
    }
});

const heroImageStylesRight = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": {
        opacity: 0,
        aspectRatio: "16/9",
        position: "absolute",
        maxWidth: "100%",
        maxHeight: "100%"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            animation: `${slideInFromRight} 0.3s ease-out forwards`
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".active-hero-image-initial": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            opacity: 1
        }
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".exiting-hero-image": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        img: {
            animation: `${slideOutToRight} 0.3s ease-out forwards`
        }
    }
});
type TabsProps = {
    tabsWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

// Memoized TabButton component for better performance
const TabButton = memo(
    ({
        tab,
        index,
        isActive,
        onTabClick
    }: {
        tab: TWCTab;
        index: number;
        isActive: boolean;
        onTabClick: (index: number) => void;
    }) => {
        const intl = useIntl();

        return (
            <Adg4DraggableTab
                key={tab.id}
                tabIndex={index}
                onClick={() => onTabClick(index)}
            >
                <button
                    css={buttonResetStyle}
                    data-product-key="home"
                    data-event-component="button"
                    data-event-container="heroSection"
                    data-event="clicked"
                    data-label-english={tab.englishLabel}
                    aria-label={intl.formatMessage(tab.title)}
                >
                    <div
                        css={[teamCardStyles, isActive && teamCardActiveStyles]}
                        data-gesture-slide-up={
                            tab.id === "plan-and-track" ||
                            tab.id === "ai-teammates"
                                ? "true"
                                : "false"
                        }
                    >
                        <div css={iconWrapperStyles}>
                            <div
                                css={[iconContainerStyles]}
                                className="default-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.icon,
                                        alt: "",
                                        draggable: "false",
                                        width: 48,
                                        height: 48
                                    }}
                                    forceEagerLoad={true} // Icons are small, load eagerly
                                />
                            </div>
                            <div
                                css={[iconContainerStyles, iconGestureStyles]}
                                className="gesture-icon"
                            >
                                <Picture
                                    image={{
                                        src: tab.iconGesture,
                                        alt: "",
                                        draggable: "false",
                                        width: 48,
                                        height: 48,
                                        loading: "eager",
                                        fetchpriority: "low"
                                    }}
                                />
                            </div>
                        </div>
                        <span css={teamCardTextStyles}>
                            {intl.formatMessage(tab.title)}
                        </span>
                    </div>
                </button>
            </Adg4DraggableTab>
        );
    }
);

TabButton.displayName = "TabButton";

// Memoized HeroImageSection component
const HeroImageSection = memo(
    ({
        isCurrentTab,
        slideDirection,
        isInitialLoad
    }: {
        isCurrentTab: (index?: number) => boolean;
        slideDirection?: "left" | "right";
        isInitialLoad: boolean;
    }) => {
        const images = (
            <>
                {TWC_TAB_DATA.map((tab, index) => {
                    return (
                        <Picture
                            key={index}
                            image={{
                                src: tab.heroImage,
                                alt: tab.englishLabel,
                                width: tab.heroImageDimensions.imageWidth,
                                height: tab.heroImageDimensions.imageHeight
                            }}
                            forceEagerLoad={index === 0} // Only first image loads eagerly
                            className={
                                isCurrentTab(index) &&
                                !(isInitialLoad && index === 0)
                                    ? "active-hero-image"
                                    : isCurrentTab(index) &&
                                      isInitialLoad &&
                                      index === 0
                                    ? "active-hero-image-initial"
                                    : ""
                            }
                        />
                    );
                })}
            </>
        );

        return (
            <Row layout="10">
                <div>
                    <div css={imageContainerStyles}>
                        {slideDirection === "left" ? (
                            <div css={heroImageStylesLeft}>{images}</div>
                        ) : (
                            <div css={heroImageStylesRight}>{images}</div>
                        )}
                        <div css={backgroundStyles} />
                    </div>
                </div>
            </Row>
        );
    }
);
HeroImageSection.displayName = "HeroImageSection";

export const TWCTabs = memo(({ tabsWrapperRef }: TabsProps) => {
    const { goToTab, isCurrentTab } = useAdg4TabsContext();
    const [previousTabIndex, setPreviousTabIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState<"left" | "right">(
        "right"
    );
    const isInitialLoadRef = useRef(true);

    const handleTabClick = useCallback(
        (index: number) => {
            isInitialLoadRef.current = false;
            // Determine direction based on previous vs current tab
            if (index > previousTabIndex) {
                setSlideDirection("right");
            } else if (index < previousTabIndex) {
                setSlideDirection("left");
            }
            setPreviousTabIndex(index);
            goToTab(index);
        },
        [previousTabIndex, goToTab]
    );

    return (
        <>
            <div css={tabsWrapperStyle} ref={tabsWrapperRef}>
                <Adg4DraggableTabList css={[tabsStyle]}>
                    {TWC_TAB_DATA.map((tab, index) => (
                        <TabButton
                            key={tab.id}
                            tab={tab}
                            index={index}
                            isActive={isCurrentTab(index)}
                            onTabClick={handleTabClick}
                        />
                    ))}
                </Adg4DraggableTabList>
            </div>
            <HeroImageSection
                isCurrentTab={isCurrentTab}
                slideDirection={slideDirection}
                isInitialLoad={isInitialLoadRef.current}
            />
        </>
    );
});

TWCTabs.displayName = "TWCTabs";
